import { GlobalStore } from '@roc/feature-app-core';
import { LoanStore } from '@roc/feature-loans';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { AppraisalQuestionnaireV2FormStore } from './appraisalQuestonnaireV2FormStore';
import { AppraisalDetailsV2Service } from '../../../services/documentForms/appraisalDetailsV2/appraisalDetailsV2Service';
import { AppraisalDetailsV2ActionsStore } from './appraisalDetailsV2ActionsStore';

export const appraisalSteps = [
  'Paid',
  'Order Placed',
  'Assigned',
  'Scheduled',
  'Inspection Done',
  'Completed',
  'Approved',
];

const STATUS_TO_STEP: Record<string, number> = {
  paid: 0,
  order_placed: 1,
  assigned: 2,
  scheduled: 3,
  inspection_done: 4,
  completed: 5,
  approved: 6,
};

const APPRAISAL_PAYMENT_LOAN = 'Appraisal_Loan';

function downloadArrayBufferAsFile(
  bytes,
  headers,
  fallbackFilename = 'signed_document.pdf'
) {
  const contentDisposition = headers?.['content-disposition'];
  const fileName = contentDisposition
    ? contentDisposition.replace(/^attachment; filename="(.+)"$/, '$1')
    : fallbackFilename;
  const blob = new Blob([bytes], { type: 'application/octet-stream' });
  const link = document.createElement('a');

  document.body.appendChild(link);
  link.setAttribute('download', fileName);
  link.setAttribute('href', URL.createObjectURL(blob));
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export function formatCurrency(val: number | null): string {
  return val != null ? '$' + val.toLocaleString() : '-';
}

function mapStatusToStep(status: string | null): number {
  if (status == null || ['pending', 'unpaid'].includes(status.toLowerCase())) return -1;
  return STATUS_TO_STEP[status.toLowerCase()] ?? 0;
}

function capitalizeFirst(value: string | null): string {
  if (!value) return '-';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function capitalizePaymentStatus(status: string): PaymentStatus {
  if (status === 'partiallyRefunded') return 'Partially Refunded';
  return status?.charAt(0).toUpperCase() + status?.slice(1) as PaymentStatus;
}

export type FeeReconciliation = {
  outcome: 'COLLECT' | 'REFUND' | 'SETTLED' | 'UNAVAILABLE';
  requiredFee: number | null;
  netPaid: number | null;
  difference: number | null;
  detail: string | null;
};

export type PaymentStatus =
  | 'Pending'
  | 'Completed'
  | 'Refunded'
  | 'Partially Refunded'
  | 'Cancelled'
  | 'Superseded';

export const paymentStillHoldsMoney = (status?: PaymentStatus | null): boolean =>
  status === 'Completed' || status === 'Partially Refunded';

export type ValuationProperty = {
  propertyId: number;
  propertyAddress: string;
  address: string;
  uuid?: string;
};

export type Valuation = {
  clientValuationReferenceId: number;
  valuationId: number;
  orderNumber: string | null;
  draftNumber: string | null;
  propertyId: number;
  valuationSequence: number | null;
  valuationStatus: string;
  paymentStatus: string;
  currentMarketValue: number | null;
  afterRepairValue: number | null;
  valuationType: string | null;
  subType: string | null;
  appraiserName: string | null;
  orderDate: string | null;
  inspectionDate: string | null;
  completionDate: string | null;
  dueDate: string | null;
  currentStep: number;
  selectedPrimary: boolean;
};

export type AppraisalReceiptData = {
  referenceId: string;
  status: 'Pending' | 'Completed';
  amount: string;
  paymentDate: string | null;
  cardHolderName: string | null;
  cardHolderEmail: string | null;
  loanId: string | null;
  purpose: string | null;
};

export class AppraisalDetailsV2Store {
  public globalStore: GlobalStore;
  public loanStore: LoanStore;
  private appraisalDetailsV2Service: AppraisalDetailsV2Service;
  public showPaymentLinkModal: boolean = false;
  questionnaireFormStore: AppraisalQuestionnaireV2FormStore;
  appraisalActionsStore: AppraisalDetailsV2ActionsStore;
  showQuestionnaire: boolean = false;
  isLoading: boolean = false;

  appraisal: any = {
    loanNumber: '',
    loanType: '',
    borrower: '',

    currentAmc: '',
  };

  properties: ValuationProperty[] = [];
  valuations: Valuation[] = [];

  payments: {
    referenceId: string;
    status: PaymentStatus;
    purpose: string;
    paidOn: string | null;
    amount: string;
    refundedAmount?: string | null;
    payableTo?: string | null;
    amountValue?: number | null;
    refundableValue?: number | null;
    allocations?: any[];
    fromDeletedValuation?: boolean;
  }[] = [];

  totalPaid: number = 0;
  pendingPayments: number = 0;
  lastPaymentDate: string | null = null;
  borrowerEmail: string = '';
  paymentLink: string = '';
  recipientEmail: string = '';
  currentPaymentId: number | null = null;
  canResetValuations: boolean = false;
  resetTier: string | null = null;
  feeReconciliation: FeeReconciliation | null = null;

  constructor(globalStore: GlobalStore, loanStore: LoanStore) {
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.appraisalDetailsV2Service = new AppraisalDetailsV2Service();
    this.questionnaireFormStore = new AppraisalQuestionnaireV2FormStore(
      globalStore,
      loanStore
    );
    this.appraisalActionsStore = new AppraisalDetailsV2ActionsStore(
      globalStore,
      loanStore
    );

    makeObservable(this, {
      appraisal: observable,
      properties: observable,
      valuations: observable,
      payments: observable,
      showQuestionnaire: observable,
      isLoading: observable,
      totalPaid: observable,
      pendingPayments: observable,
      lastPaymentDate: observable,
      borrowerEmail: observable,
      paymentLink: observable,
      recipientEmail: observable,
      currentPaymentId: observable,
      questionnaireFormStore: observable,
      setShowQuestionnaire: action,
      fetchAppraisalDetails: flow,
      fetchPaymentLink: flow,
      fetchRecertificationPaymentLink: flow,
      fetchPaymentReceiptData: flow,
      downloadPaymentAuthorization: flow,
      pendingPaymentsCount: computed,
      paymentSummary: computed,
      resolvedBorrowerEmail: computed,
      setShowPaymentLinkModal: action,
      showPaymentLinkModal: observable,
      setPaymentLink: action,
      reset: action,
      resetValuationsByLoanId: flow,
      refundPayment: flow,
      resendPaymentEmail: flow,
      editPayment: flow,
      fetchResetCapability: flow,
      fetchFeeReconciliation: flow,
      canResetValuations: observable,
      resetTier: observable,
      feeReconciliation: observable
    });
  }

  *fetchFeeReconciliation(loanId?: number) {
    if (!loanId || !this.globalStore.isInternalPortal) {
      this.feeReconciliation = null;
      return;
    }
    try {
      const response = yield this.appraisalDetailsV2Service.reconcileValuationFees(loanId);
      this.feeReconciliation = response?.data?.data ?? null;
    } catch (e) {
      this.feeReconciliation = null;
    }
  }

  *fetchResetCapability(loanId?: number) {
    if (!loanId || !this.globalStore.isInternalPortal) {
      this.canResetValuations = false;
      this.resetTier = null;
      return;
    }
    try {
      const response = yield this.appraisalDetailsV2Service.canResetValuations(loanId);
      this.canResetValuations = !!response?.data?.data?.allowed;
      this.resetTier = response?.data?.data?.tier ?? null;
    } catch (e) {
      this.canResetValuations = false;
      this.resetTier = null;
    }
  }

  *fetchAppraisalDetails(loanId?: number, loanPropertyValuationId?: number) {
    this.isLoading = true;
    try {
      const response = yield this.appraisalDetailsV2Service.getValuationDetails(loanId, loanPropertyValuationId);
      const data = response.data.data;

      this.appraisal = {
        loanNumber: data.loanId,
        lenderId: data.lenderId,
        loanType: data.loanSubType,
        borrower: data.borrowerName,
        currentAmc: data.currentAmc,
      };

      this.properties = data.properties.map((p: any) => ({
        propertyId: p.propertyId,
        propertyAddress: p.propertyAddress ?? `Property #${p.propertyId}`,
      }));

      this.valuations = data.valuations.map((v: any) => ({
        clientValuationReferenceId: v.clientValuationReferenceId,
        valuationId: v.valuationId,
        orderNumber: v.orderNumber,
        draftNumber: v.draftNumber,
        propertyId: v.propertyId,
        valuationSequence: v.valuationSequence,
        valuationStatus: capitalizeFirst(v.valuationStatus),
        paymentStatus: capitalizeFirst(v.paymentStatus),
        currentMarketValue: v.currentMarketValue,
        afterRepairValue: v.afterRepairValue,
        valuationType: v.valuationType,
        subType: v.subType,
        appraiserName: v.appraiserName,
        orderDate: v.orderDate,
        inspectionDate: v.inspectionDate,
        completionDate: v.completionDate,
        dueDate: v.dueDate,
        currentStep: mapStatusToStep(v.valuationStatus),
        selectedPrimary: v.selectedPrimary ?? false,
      }));

      this.payments = data.valuationPayments.map((p: any) => ({
        referenceId: String(p.valuationPaymentId),
        status: capitalizePaymentStatus(p.status),
        purpose: p.purpose,
        paidOn: p.paidOn,
        amount: p.amount != null ? formatCurrency(p.amount) : '-',
        refundedAmount: p.refundedAmount != null ? formatCurrency(p.refundedAmount) : null,
        payableTo: p.payableTo ?? null,
        amountValue: p.amount ?? null,
        refundableValue: p.amount != null ? Math.max(p.amount - (p.refundedAmount ?? 0), 0) : null,
        allocations: Array.isArray(p.allocations) ? p.allocations : [],
        fromDeletedValuation: p.fromDeletedValuation === true,
      }));

      this.totalPaid = data.totalPaid;
      this.pendingPayments = data.pendingPayments;
      this.lastPaymentDate = data.lastPaymentDate;
      this.borrowerEmail = data.borrowerEmail ?? '';
      this.showQuestionnaire = !data.questionnaireCompleted;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load valuation details',
      });
    } finally {
      this.isLoading = false;
    }
  }

  *fetchPaymentLink(loanId: number, paymentId: number, lenderId?: number, clientReferenceId?: number) {
    this.paymentLink = '';
    this.recipientEmail = '';
    this.currentPaymentId = paymentId ?? null;
    try {
      const clientValuationReferenceIds = this.valuations.length > 0
        ? this.valuations.map(v => v.clientValuationReferenceId)
        : clientReferenceId != null ? [clientReferenceId] : [];
      const response = yield this.appraisalDetailsV2Service.getValuationPaymentLink(
        loanId,
        this.appraisal.lenderId || lenderId,
        paymentId,
        clientValuationReferenceIds
      );
      const data = response.data.data;
      this.paymentLink = data?.paymentLink ?? '';
      this.recipientEmail = data?.recipientEmail ?? '';
      this.currentPaymentId = this.currentPaymentId ?? data?.paymentId ?? null;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load payment link',
      });
    }
  }

  *fetchRecertificationPaymentLink(loanId: number, propertyId: number) {
    this.paymentLink = '';
    this.recipientEmail = '';
    try {
      const response = yield this.appraisalDetailsV2Service.getRecertificationPaymentLink(loanId, propertyId);
      const data = response.data.data;
      this.paymentLink = data?.paymentLink ?? '';
      this.recipientEmail = data?.recipientEmail ?? '';
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load recertification payment link',
      });
    }
  }

  *downloadPaymentAuthorization(referenceId?: string) {
    try {
      const response = yield this.appraisalDetailsV2Service.downloadSignedDocument(
        referenceId,
      );

      downloadArrayBufferAsFile(
        response?.data,
        response?.headers,
        'signed_document.pdf'
      );
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while downloading payment authorization.',
      });
    }
  }

  *editPayment(payment?: any) {
    let parsedAmount;
    try{
      parsedAmount = typeof payment?.amount === 'string' ?
      Number(payment?.amount?.replace(/[^0-9.-]+/g, "")) : payment?.amount;
      if(!parsedAmount || isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error();
      }
    }catch(e){
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please enter a valid amount',
      });
      throw e;
    }

    try {
      yield this.appraisalDetailsV2Service.editPayment({
        ...payment,
        valuationPaymentId: payment?.referenceId
      });
      yield this.fetchAppraisalDetails(this.loanStore.loanDetails?.loanId ?? 0);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payment edited successfully',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error editing the payment',
      });
      throw e;
    }
  }

  *fetchPaymentReceiptData(paymentId: string) {
    if (!paymentId) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to determine the payment receipt to download.',
      });
      return null;
    }

    try {
      const response = yield this.appraisalDetailsV2Service.getPaymentReceiptData(
        Number(paymentId)
      );
      const data = response?.data?.data;
      if (!data) {
        return null;
      }

      const amountValue = data.amount != null ? Number(data.amount) : null;

      return {
        referenceId: String(data.referenceNumber ?? paymentId),
        status: capitalizePaymentStatus(data.status),
        amount: amountValue != null && !Number.isNaN(amountValue)
          ? formatCurrency(amountValue)
          : '-',
        paymentDate: data.paymentDate ?? null,
        cardHolderName: data.cardHolderName ?? null,
        cardHolderEmail: data.cardHolderEmail ?? null,
        loanId: data.loanId != null ? String(data.loanId) : null,
        purpose: data.purpose ?? null,
      } as AppraisalReceiptData;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while loading receipt details.',
      });
      return null;
    }
  }

  setShowQuestionnaire(show: boolean) {
    this.showQuestionnaire = show;
  }

  reset() {
    this.questionnaireFormStore.reset();
    this.showQuestionnaire = false;
    this.isLoading = false;
    this.properties = [];
    this.valuations = [];
    this.payments = [];
    this.totalPaid = 0;
    this.pendingPayments = 0;
    this.lastPaymentDate = null;
    this.borrowerEmail = '';
    this.paymentLink = '';
    this.recipientEmail = '';
    this.currentPaymentId = null;
    this.showPaymentLinkModal = false;
    this.canResetValuations = false;
    this.resetTier = null;
    this.feeReconciliation = null;
  }

  setShowPaymentLinkModal(value: boolean) {
    this.showPaymentLinkModal = value;
  }

  setPaymentLink(link: string) {
    this.paymentLink = link;
  }

  get pendingPaymentsCount() {
    return this.payments.filter(p => p.status === 'Pending').length;
  }

  get paymentSummary(): { label: string; count?: number } {
    if (this.payments.length === 0) return { label: 'None' };
    if (this.pendingPaymentsCount > 0) return { label: 'Pending', count: this.pendingPaymentsCount };
    if (this.pendingPaymentsCount === 0) return { label: 'Completed' };
    return { label: 'N/A' };
  }

  get resolvedBorrowerEmail(): string {
    if (this.borrowerEmail) return this.borrowerEmail;
    const borrowers = this.loanStore?.loanDetails?.borrowers;
    return (Array.isArray(borrowers) && borrowers.length > 0)
      ? (borrowers[0]?.emailAddress ?? '')
      : '';
  }

  *resendPaymentEmail() {
    try {
      yield this.appraisalDetailsV2Service.resendPaymentEmail(this.currentPaymentId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payment email sent to borrower successfully',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to send payment email to borrower',
      });
    }
  }

  *resetValuationsByLoanId(loanId: number) {
    try {
      yield this.appraisalDetailsV2Service.resetValuationsByLoanId(loanId);
    } catch (e) {
      const status = e?.error?.response?.status;
      const serverMessage = e?.error?.response?.data?.error?.message;
      this.globalStore.notificationStore.showErrorNotification({
        message:
          status === 403
            ? serverMessage || 'You do not have permission to reset the valuations on this loan'
            : status === 409
            ? serverMessage || 'A reset of this loan is already running'
            : 'Failed while reseting valuations',
      });
      throw e;
    }
  }

  refundableAmountFor(paymentId: any): number | null {
    const payment = this.payments.find(p => p.referenceId === String(paymentId));
    return payment?.refundableValue ?? null;
  }

  *refundPayment(paymentId: any, amount: any) {
    let parsedAmount;
    try{
      parsedAmount = typeof amount === 'string' ?
      Number(amount?.replace(/[^0-9.-]+/g, "")) : amount;
      if(!parsedAmount || isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error();
      }
    }catch(e){
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please enter a valid refund amount',
      });
      throw e;
    }

    const refundable = this.refundableAmountFor(paymentId);
    if (refundable != null && parsedAmount > refundable) {
      this.globalStore.notificationStore.showWarningNotification({
        message: `Only ${formatCurrency(refundable)} of this payment is still held and can be refunded`,
      });
      throw new Error('Refund amount exceeds what the payment still holds');
    }

    try{
      yield this.appraisalDetailsV2Service.refundPayment({ paymentId, amount: parsedAmount });
      yield this.fetchAppraisalDetails(this.loanStore.loanDetails?.loanId ?? 0);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payment refunded successfully',
      });
    }catch(e){
      this.globalStore.notificationStore.showErrorNotification({
        message: e?.error?.response?.data?.error?.message || 'Failed to refund payment',
      });
      throw e;
    }
  }
}
